import { useState, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  FileText,
  Clock,
  CheckCircle2,
  Files,
  Search,
  List,
  CircleCheckBig,
  ListChecks,
  HelpCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import PtsProjectService from "@/services/APIs/PtsProjectService";
import DropdownService from "@/services/APIs/DropdownService";
import { notify } from "@/components/common/Notification";

const PTS_BILLING_TYPE_SCHEMA_NAME = "PTS Billing Type";
const PTS_TASK_CATEGORY_SCHEMA_NAME = "PTS Task Category";

/** Normalized template for UI (from API project-template response) */
type TemplateItem = {
  id: string;
  project_id: string | null;
  title: string;
  description: string;
  category: string;
  duration: string | null;
  tasks: number;
  billing: string;
  billing_model: string;
  defaultTasks?: Array<{ name: string; priority: string; hours: number }>;
  defaultMilestones?: Array<{
    name: string;
    description: string;
    billing: number;
  }>;
  workflowStages?: string[];
  createdBy?: string;
};

/** Task from project-details API task_listing */
type TaskListingItem = {
  id: string;
  taskname: string;
  taskestimatedhours?: string;
  actualhours?: string;
  variance?: string;
  parent_task?: string | null;
  taskcategory?: string;
  category?: string;
  task_category?: string;
  subtask?: TaskListingItem[];
};

const ProjectTemplatesPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedBilling] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateItem | null>(
    null
  );
  const [templateToUse, setTemplateToUse] = useState<TemplateItem | null>(null);

  const detailProjectId = selectedTemplate?.project_id ?? null;
  const isConfirmOpen = templateToUse !== null;

  const cloneMutation = useMutation({
    mutationFn: (projectId: string) =>
      PtsProjectService.cloneProjectFromTemplate(projectId),
    onSuccess: () => {
      setTemplateToUse(null);
      handleCloseModal();
      notify.success(
        "Template applied",
        "Project has been created from this template successfully."
      );
    },
    onError: (error: Error) => {
      notify.error("Failed to use template", error.message);
      setTemplateToUse(null);
      handleCloseModal();
    },
  });

  const handleUseTemplateClick = (template: TemplateItem) => {
    if (!template.project_id) {
      return;
    }
    setTemplateToUse(template);
  };

  const handleConfirmUseTemplate = () => {
    if (!templateToUse?.project_id) {
      return;
    }
    cloneMutation.mutate(templateToUse.project_id);
  };

  const handleCancelConfirm = () => {
    if (!cloneMutation.isPending) {
      setTemplateToUse(null);
    }
  };

  const { data: templatesResponse, isLoading } = useQuery({
    queryKey: ["project-templates"],
    queryFn: () => PtsProjectService.getProjectTemplates(),
  });

  const { data: billingTypeOptions } = useQuery({
    queryKey: ["dropdown", PTS_BILLING_TYPE_SCHEMA_NAME],
    queryFn: () =>
      DropdownService.getDropdownData(PTS_BILLING_TYPE_SCHEMA_NAME),
  });

  const { data: projectDetailsResponse, isLoading: isDetailsLoading } =
    useQuery({
      queryKey: ["project-template-details", detailProjectId],
      queryFn: () =>
        PtsProjectService.getProjectTemplateDetails(detailProjectId!),
      enabled: isModalOpen && !!detailProjectId,
    });

  const projectDetails = projectDetailsResponse?.data ?? null;

  const { data: taskCategoryOptions } = useQuery({
    queryKey: ["dropdown", PTS_TASK_CATEGORY_SCHEMA_NAME],
    queryFn: () =>
      DropdownService.getDropdownData(PTS_TASK_CATEGORY_SCHEMA_NAME),
    enabled: isModalOpen && !!detailProjectId,
  });

  const categoryIdToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    const options = Array.isArray(taskCategoryOptions)
      ? taskCategoryOptions
      : ((taskCategoryOptions as { results?: unknown[]; data?: unknown[] })
          ?.results ??
        (taskCategoryOptions as { results?: unknown[]; data?: unknown[] })
          ?.data ??
        []);
    (
      options as {
        value?: string;
        label?: string;
        id?: string;
        name?: string;
      }[]
    ).forEach(opt => {
      const id = String(opt?.value ?? opt?.id ?? "");
      if (id) {
        map[id] = String(opt?.label ?? opt?.name ?? id);
      }
    });
    return map;
  }, [taskCategoryOptions]);

  const billingOptionsList = useMemo(() => {
    const raw = billingTypeOptions;
    if (!raw) {
      return [];
    }
    return Array.isArray(raw) ? raw : [];
  }, [billingTypeOptions]);

  const getBillingNameById = (billingId: string): string => {
    if (!billingId) {
      return "—";
    }
    const option = billingOptionsList.find(
      (item: { value?: string; label?: string; id?: string; name?: string }) =>
        String(item?.value ?? item?.id ?? "") === String(billingId)
    );
    return (option?.label ?? option?.name ?? billingId) as string;
  };

  const templates: TemplateItem[] = useMemo(() => {
    const raw = templatesResponse?.data;
    if (!raw || !Array.isArray(raw)) {
      return [];
    }
    return raw.map((item, index) => ({
      id: `${item.billing_model}-${item.projectname}-${index}`,
      project_id: item.id ?? item.project_id ?? null,
      title: item.projectname ?? "",
      description: item.description ?? "",
      category: item.project_type ?? "",
      duration: item.duration ?? null,
      tasks: item["total-task"] ?? 0,
      billing: item.billing_model ?? "",
      billing_model: item.billing_model ?? "",
    }));
  }, [templatesResponse?.data]);

  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    templates.forEach(t => {
      if (t.category) {
        set.add(t.category);
      }
    });
    return ["all", ...Array.from(set).sort()];
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesSearch =
        template.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || template.category === selectedCategory;
      const matchesBilling =
        selectedBilling === "all" || template.billing === selectedBilling;
      return matchesSearch && matchesCategory && matchesBilling;
    });
  }, [templates, searchQuery, selectedCategory, selectedBilling]);

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case "external":
        return "bg-primary text-white";
      case "internal":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleDetailsClick = (template: TemplateItem) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const formatTaskHours = (hours: string | null | undefined): string => {
    if (
      hours === null ||
      hours === "" ||
      String(hours).trim() === "" ||
      hours === "0:00"
    ) {
      return "0h";
    }
    const trimmed = String(hours).trim();
    if (trimmed === "" || trimmed === "0:00") {
      return "0h";
    }
    return trimmed.endsWith("h") ? trimmed : `${trimmed}h`;
  };

  const getTaskCategoryLabel = (task: TaskListingItem): string => {
    const catId = task.taskcategory ?? task.category ?? task.task_category;
    if (!catId) {
      return "-";
    }
    return categoryIdToLabel[String(catId)] ?? "-";
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Header Section */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Project Templates
          </h1>
          <p className="text-sm text-[#627384]">
            Create and manage reusable project templates
          </p>
        </div>
        {/* <Button className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white">
          <Plus className="h-4 w-4" />
          Create Template
        </Button> */}
      </div>

      {/* Summary Cards */}
      {/* <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                <FileText className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Templates</p>
                <p className="text-2xl font-bold text-[#111827]">
                  {totalTemplates}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                <Rocket className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Starter Packs</p>
                <p className="text-2xl font-bold text-[#111827]">
                  {starterPacks}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                <Building2 className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Org Library</p>
                <p className="text-2xl font-bold text-[#111827]">
                  {orgLibrary}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                <Settings className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Custom</p>
                <p className="text-2xl font-bold text-[#111827]">{custom}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                <Target className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Usage</p>
                <p className="text-2xl font-bold text-green-600">
                  {totalUsage}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Filter and Search Section */}
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardContent className="p-4 md:p-6">
          <div className="flex w-[80%] flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-1 flex-col gap-6 sm:flex-row sm:gap-4">
              {/* Search Input */}
              <div className="min-w-0 flex-1">
                <label className="mb-2 block text-sm font-semibold text-[#111827]">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="bg-background-input border-border-input focus:bg-background-focus focus:border-primary rounded-xl pl-10"
                  />
                </div>
              </div>

              {/* Category Dropdown (project_type from API) */}
              <div className="min-w-0 flex-1">
                <label className="mb-2 block text-sm font-semibold text-[#111827]">
                  Category
                </label>
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger className="bg-background-input border-border-input focus:bg-background-focus focus:border-primary w-full rounded-xl">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent className="bg-background-input border-border-input focus:bg-background-focus focus:border-primary rounded-xl">
                    <SelectItem value="all">All Categories</SelectItem>
                    {categoryOptions
                      .filter(c => c !== "all")
                      .map(cat => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Billing Model Dropdown */}
              {/* <div className="min-w-0 flex-1">
                <label className="mb-2 block text-sm font-semibold text-[#111827]">
                  Billing Model
                </label>
                <Select
                  value={selectedBilling}
                  onValueChange={setSelectedBilling}
                >
                  <SelectTrigger className="bg-background-input border-border-input focus:bg-background-focus focus:border-primary w-full rounded-xl">
                    <SelectValue placeholder="All Models" />
                  </SelectTrigger>
                  <SelectContent className="bg-background-input border-border-input focus:bg-background-focus focus:border-primary rounded-xl">
                    <SelectItem value="all">All Models</SelectItem>
                    <SelectItem value="Milestone">Milestone</SelectItem>
                    <SelectItem value="Fixed Price">Fixed Price</SelectItem>
                    <SelectItem value="Hourly">Hourly</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
            </div>

            {/* Templates Count Badge */}
            <div className="flex items-end sm:ml-4">
              <div className="whitespace-nowrap rounded-full bg-gray-100 px-4 py-2">
                <span className="text-sm font-medium text-[#111827]">
                  {filteredTemplates.length} template
                  {filteredTemplates.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Template Cards Grid */}
      {!isLoading && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map(template => (
            <Card
              key={template.id}
              className="bg-white transition-shadow duration-200 hover:shadow-lg"
            >
              <CardContent className="relative p-6">
                {/* Project type badge – fixed top-right of card */}
                <div className="absolute right-4 top-4">
                  <Badge
                    className={`${getCategoryBadgeColor(
                      template.category
                    )} text-xs`}
                  >
                    {template.category === "internal" ? "Internal" : "External"}
                  </Badge>
                </div>

                <div className="flex items-center gap-3 pr-20">
                  <div className="bg-primary/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg">
                    <FileText className="text-primary h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#111827]">
                    {template.title}
                  </h3>
                </div>

                <p className="mt-3 text-sm text-gray-600">
                  {template.description}
                </p>

                <div className="mt-4 grid gap-2 border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {template.duration ?? "-"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      <span className="text-primary font-medium">
                        {template.tasks}
                      </span>{" "}
                      Tasks
                    </span>
                  </div>
                  {template.category !== "internal" && (
                    <div className="flex items-center gap-2">
                      <CircleCheckBig className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        Billing:{" "}
                        <span className="text-primary rounded-md border border-gray-200 px-2 text-xs font-medium">
                          {getBillingNameById(template.billing_model)}
                        </span>
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
                  <span className="text-sm text-gray-500"> Used 0 times</span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Details"
                      onClick={() => handleDetailsClick(template)}
                    >
                      <Files className="h-4 w-4" />
                    </Button>
                    {/* <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Copy"
                    >
                      <Copy className="h-4 w-4" />
                    </Button> */}
                    <Button
                      className="bg-primary hover:bg-primary/90 h-8 px-4 text-xs text-white"
                      onClick={() => handleUseTemplateClick(template)}
                      disabled={!template.project_id}
                    >
                      Use
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && filteredTemplates.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-gray-400" />
          <p className="mt-4 text-lg font-medium text-gray-600">
            No templates found
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}

      {/* Confirm Use Template Modal */}
      <Dialog
        open={isConfirmOpen}
        onOpenChange={open => !open && handleCancelConfirm()}
      >
        <DialogContent className="max-w-md gap-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="bg-primary/10 flex h-14 w-14 items-center justify-center rounded-full">
              <HelpCircle className="text-primary h-7 w-7" />
            </div>
            <div className="space-y-2">
              <DialogTitle className="text-lg font-semibold text-[#111827]">
                Use this template?
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-600">
                Are you sure you want to use the template{" "}
                <span className="font-medium text-[#111827]">
                  {templateToUse?.title ?? "this template"}
                </span>
                ? A new project will be created from it.
              </DialogDescription>
            </div>
          </div>
          <DialogFooter className="flex gap-3 sm:justify-center">
            <Button
              variant="outline"
              onClick={handleCancelConfirm}
              disabled={cloneMutation.isPending}
              className="rounded-xl border-gray-300 bg-white px-5 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUseTemplate}
              disabled={cloneMutation.isPending}
              className="bg-primary hover:bg-primary/90 rounded-xl px-5 text-white"
            >
              {cloneMutation.isPending ? "Creating…" : "Yes, use template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Detail Modal – fetches project-details by project_id when open */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white">
          {selectedTemplate && (
            <div>
              {isDetailsLoading && detailProjectId ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : (
                <div>
                  <DialogHeader className="relative pb-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-primary/10 flex h-12 w-12 items-center justify-center rounded-lg">
                        <FileText className="text-primary h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <DialogTitle className="text-primary text-xl font-semibold">
                          {projectDetails?.["project-name"] ??
                            selectedTemplate.title}
                        </DialogTitle>
                        <DialogDescription className="text-gray-600">
                          {projectDetails?.description ??
                            selectedTemplate.description}
                        </DialogDescription>
                        <Badge
                          className={`${getCategoryBadgeColor(
                            projectDetails?.project_type ??
                              selectedTemplate.category
                          )} mt-2 text-xs`}
                        >
                          {projectDetails?.project_type ??
                            selectedTemplate.category}
                        </Badge>
                      </div>
                    </div>
                  </DialogHeader>

                  {/* Project Overview Cards */}
                  <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Card className="border-0 bg-gray-50 shadow-md">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-600" />
                          <div>
                            <p className="text-xs text-gray-500">Duration</p>
                            <p className="text-sm font-semibold text-[#111827]">
                              {projectDetails?.duration ??
                                selectedTemplate.duration ??
                                "—"}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    {(projectDetails?.project_type ??
                      selectedTemplate.category) !== "internal" && (
                      <Card className="border-0 bg-gray-50 shadow-md">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-gray-600" />
                            <div>
                              <p className="text-xs text-gray-500">
                                Billing Model
                              </p>
                              <p className="text-sm font-semibold text-[#111827]">
                                {getBillingNameById(
                                  projectDetails?.billing_model ??
                                    selectedTemplate.billing_model
                                )}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    <Card className="border-0 bg-gray-50 shadow-md">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <ListChecks className="h-4 w-4 text-gray-600" />
                          <div>
                            <p className="text-xs text-gray-500">Tasks</p>
                            <p className="text-sm font-semibold text-[#111827]">
                              {projectDetails?.task_listing?.length ??
                                selectedTemplate.tasks}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Task listing from project-details API – card design: name + category pill (left), estimated hours (right) */}
                  {projectDetails?.task_listing &&
                    projectDetails.task_listing.length > 0 && (
                      <div className="mb-6">
                        <div className="mb-4 flex items-center gap-2">
                          <List className="h-5 w-5 text-gray-600" />
                          <h3 className="text-lg font-semibold text-[#111827]">
                            Tasks ({projectDetails.task_listing.length})
                          </h3>
                        </div>
                        <div className="space-y-2">
                          {(
                            projectDetails.task_listing as TaskListingItem[]
                          ).map(task => {
                            const renderTaskCard = (
                              t: TaskListingItem,
                              isSubtask: boolean
                            ) => {
                              const name = t.taskname ?? "—";
                              const catLabel = getTaskCategoryLabel(t);
                              const hours = formatTaskHours(
                                t.taskestimatedhours
                              );
                              return (
                                <div
                                  key={t.id}
                                  className={
                                    isSubtask
                                      ? "flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm"
                                      : "border-primary/30 mt-5 flex items-center justify-between gap-4 rounded-lg border bg-gray-100/90 px-4 py-3"
                                  }
                                >
                                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                                    <span className="truncate text-sm font-semibold text-gray-900">
                                      {name}
                                    </span>
                                    <span className="inline-flex shrink-0 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                                      {catLabel}
                                    </span>
                                  </div>
                                  <span className="shrink-0 text-sm text-gray-600">
                                    {hours}
                                  </span>
                                </div>
                              );
                            };
                            return (
                              <div key={task.id}>
                                {renderTaskCard(task, false)}
                                {task.subtask && task.subtask.length > 0 && (
                                  <div className="ml-4 mt-2 space-y-2 border-l-2 border-gray-200 pl-3">
                                    {task.subtask.map((sub: TaskListingItem) =>
                                      renderTaskCard(sub, true)
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {!detailProjectId && (
                    <p className="mb-4 text-sm text-gray-500">
                      Project ID not available; full details not loaded.
                    </p>
                  )}

                  <DialogFooter className="mt-6 flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={handleCloseModal}
                      className="bg-gray-100 text-[#111827] hover:bg-gray-200"
                    >
                      Close
                    </Button>
                    <Button
                      onClick={() =>
                        selectedTemplate &&
                        handleUseTemplateClick(selectedTemplate)
                      }
                      disabled={!detailProjectId}
                      className="bg-primary hover:bg-primary/90 text-white"
                    >
                      Use Template
                    </Button>
                  </DialogFooter>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectTemplatesPage;
